import React, { useState } from 'react';
import { User, Lock, AlertCircle, KeyRound, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { solicitarCodigoRecuperacion, confirmarRecuperacion } from '../lib/auth';

const INPUT =
  'w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition';
const BOTON =
  'w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

interface Props {
  onVolver: () => void;
}

/**
 * Recuperación en dos pasos mediante código de un solo uso.
 *
 * Se usa código en vez de enlace porque el correo se abre en el navegador del
 * sistema, que no puede devolver la sesión a la app de escritorio. Así el
 * proceso entero ocurre dentro de la aplicación y no depende de URLs de
 * redirección, con lo que se comporta igual en desarrollo y en producción.
 */
export function RecuperarPassword({ onVolver }: Props) {
  const [paso, setPaso] = useState<'solicitar' | 'confirmar' | 'hecho'>('solicitar');
  const [identificador, setIdentificador] = useState('');
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [password, setPassword] = useState('');
  const [repetir, setRepetir] = useState('');
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);

  const pedirCodigo = async (e: React.FormEvent) => {
    e.preventDefault();
    setCargando(true);
    setError('');
    try {
      const destino = await solicitarCodigoRecuperacion(identificador);
      setEmail(destino);
      setPaso('confirmar');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido enviar el código.');
    } finally {
      setCargando(false);
    }
  };

  const confirmar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) return setError('La contraseña debe tener al menos 6 caracteres.');
    if (password !== repetir) return setError('Las contraseñas no coinciden.');

    setCargando(true);
    setError('');
    try {
      await confirmarRecuperacion(email, codigo, password);
      setPaso('hecho');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido cambiar la contraseña.');
    } finally {
      setCargando(false);
    }
  };

  // Se muestra parcialmente oculto: confirma al usuario a qué buzón mirar sin
  // revelar la dirección completa a quien no debería conocerla.
  const emailOculto = email.replace(/^(.{2}).*(@.*)$/, '$1•••$2');

  if (paso === 'hecho') {
    return (
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center w-12 h-12 bg-green-50 rounded-full mb-5">
          <CheckCircle2 className="w-6 h-6 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900">Contraseña actualizada</h2>
        <p className="mt-1 mb-8 text-slate-500 text-sm">
          Ya puedes acceder con tu nueva contraseña.
        </p>
        <button onClick={onVolver} className={BOTON}>Ir a iniciar sesión</button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm">
      <button
        onClick={paso === 'confirmar' ? () => { setPaso('solicitar'); setError(''); } : onVolver}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {paso === 'confirmar' ? 'Usar otro usuario' : 'Volver al inicio de sesión'}
      </button>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-slate-900">
          {paso === 'solicitar' ? 'Recuperar contraseña' : 'Introduce el código'}
        </h2>
        <p className="mt-1 text-slate-500 text-sm">
          {paso === 'solicitar'
            ? 'Te enviaremos un código de verificación a tu correo.'
            : <>Hemos enviado un código de verificación a <span className="font-medium text-slate-700">{emailOculto}</span>. Cópialo entero. Caduca en una hora.</>}
        </p>
      </div>

      {paso === 'solicitar' ? (
        <form className="space-y-5" onSubmit={pedirCodigo}>
          <div>
            <label htmlFor="identificador" className="block text-sm font-medium text-slate-700 mb-1.5">
              Usuario o correo electrónico
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                id="identificador"
                type="text"
                required
                autoComplete="username"
                className={INPUT}
                placeholder="Tu usuario o tu correo"
                value={identificador}
                onChange={(e) => setIdentificador(e.target.value)}
              />
            </div>
          </div>

          {error && <Aviso texto={error} />}

          <button type="submit" disabled={cargando} className={BOTON}>
            {cargando ? 'Enviando…' : 'Enviar código'}
          </button>
        </form>
      ) : (
        <form className="space-y-5" onSubmit={confirmar}>
          <div>
            <label htmlFor="codigo" className="block text-sm font-medium text-slate-700 mb-1.5">
              Código de verificación
            </label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                id="codigo"
                type="text"
                required
                inputMode="numeric"
                autoComplete="one-time-code"
                // Sin límite ajustado a una longitud concreta: Supabase permite
                // configurar el tamaño del OTP (este proyecto lo tiene en 8, no
                // en los 6 que trae por defecto). Un maxLength corto trunca el
                // código en silencio y la verificación falla sin explicar por qué.
                maxLength={12}
                className={`${INPUT} tracking-[0.3em] font-mono`}
                placeholder="Código recibido"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
              />
            </div>
          </div>

          <div>
            <label htmlFor="nueva" className="block text-sm font-medium text-slate-700 mb-1.5">
              Nueva contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                id="nueva"
                type="password"
                required
                autoComplete="new-password"
                className={INPUT}
                placeholder="Mínimo 6 caracteres"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label htmlFor="repetir" className="block text-sm font-medium text-slate-700 mb-1.5">
              Repite la contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                id="repetir"
                type="password"
                required
                autoComplete="new-password"
                className={INPUT}
                placeholder="Repite la nueva contraseña"
                value={repetir}
                onChange={(e) => setRepetir(e.target.value)}
              />
            </div>
          </div>

          {error && <Aviso texto={error} />}

          <button type="submit" disabled={cargando} className={BOTON}>
            {cargando ? 'Guardando…' : 'Cambiar contraseña'}
          </button>
        </form>
      )}
    </div>
  );
}

function Aviso({ texto }: { texto: string }) {
  return (
    <div className="flex items-start gap-2.5 p-3 bg-red-50 border border-red-100 rounded-lg">
      <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
      <p className="text-sm text-red-700">{texto}</p>
    </div>
  );
}
